<template>
  <v-row class="fill-height">
    <ServiceColorLegend />
    <v-col>
      <v-sheet height="64">
        <v-toolbar color="white" flat>
          <v-btn
            class="mr-4"
            color="grey darken-2"
            outlined
            small
            @click="setToday()"
          >
            Tänasesse päeva
          </v-btn>
          <v-btn color="grey darken-2" @click="prev" fab small text>
            <v-icon small>
              mdi-chevron-left
            </v-icon>
          </v-btn>
          <v-btn color="grey darken-2" @click="next" fab small text>
            <v-icon small>
              mdi-chevron-right
            </v-icon>
          </v-btn>
          <v-toolbar-title>
            {{ monthString }}
          </v-toolbar-title>
          <v-spacer />
          <v-menu bottom right>
            <template v-slot:activator="{ on, attrs }">
              <v-btn color="grey darken-2" outlined v-bind="attrs" v-on="on">
                <span>{{ typeToLabel[type] }}</span>
                <v-icon right>
                  mdi-menu-down
                </v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item @click="type = 'month'">
                <v-list-item-title>Kuu</v-list-item-title>
              </v-list-item>
              <v-list-item @click="type = 'week'">
                <v-list-item-title>Nädal</v-list-item-title>
              </v-list-item>
              <v-list-item @click="type = '4day'">
                <v-list-item-title>4 päeva</v-list-item-title>
              </v-list-item>
              <v-list-item @click="type = 'day'">
                <v-list-item-title>Päev</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-toolbar>
      </v-sheet>
      <v-sheet height="580">
        <v-calendar
          ref="calendar"
          v-model="focus"
          :weekdays="[1, 2, 3, 4, 5, 6, 0]"
          :event-color="getEventColor"
          :events="events"
          :type="type"
          color="secondary"
          @click:date="viewDay"
          @click:event="showEvent"
          @click:more="viewDay"
        >
          <!-- Single event -->
          <template v-slot:event="e">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon
                  v-on="on"
                  v-if="e.event.priorityClass === 'priority40'"
                  color="red darken-4"
                  class="priority-icon"
                  >mdi-chevron-double-up</v-icon
                >
              </template>
              <span>Kriitiline</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon
                  v-on="on"
                  v-if="e.event.priorityClass === 'priority30'"
                  color="red darken-4"
                  class="priority-icon"
                  >mdi-chevron-up</v-icon
                >
              </template>
              <span>Kõrge</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon
                  v-on="on"
                  v-if="e.event.priorityClass === 'priority20'"
                  color="green darken-2"
                  class="priority-icon"
                  >mdi-chevron-right</v-icon
                >
              </template>
              <span>Keskmine</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon
                  v-on="on"
                  v-if="e.event.priorityClass === 'priority10'"
                  color="green darken-2"
                  class="priority-icon"
                  >mdi-chevron-down</v-icon
                >
              </template>
              <span>Madal</span>
            </v-tooltip>
            <span :class="[e.event.priorityClass, 'event-name']">
              {{ e.eventParsed.start.time }} {{ e.event.name }}
            </span>
          </template>
        </v-calendar>

        <ActionCard
          v-model="selectedOpen"
          :selected-action="selectedAction"
          :selected-element="selectedElement"
        />
      </v-sheet>
    </v-col>
  </v-row>
</template>

<script>
import Action from '@/dataAccess/dto/Action'
import ServiceColorLegend from '@/components/Calendar/ServiceColorLegend'
import { getLocalMonthString } from '@/utils/datetime'
import ActionCard from '@/components/Calendar/ActionCard'

export default {
  components: { ActionCard, ServiceColorLegend },
  props: {
    personType: {
      type: String,
      required: true
    },
    personId: {
      required: true
    }
  },
  data: () => ({
    focus: '',
    type: 'month',
    typeToLabel: {
      month: 'Kuu',
      week: 'Nädal',
      '4day': '4 päeva',
      day: 'Päev'
    },
    selectedAction: {},
    selectedMonth: new Date(),
    monthString: '',
    selectedElement: null,
    selectedOpen: false,
    events: []
  }),
  mounted() {
    this.$refs.calendar.checkChange()
    this.update()
  },
  methods: {
    viewDay({ date }) {
      this.focus = date
      this.type = 'day'
    },
    getEventColor(event) {
      return event.color
    },
    setToday() {
      this.focus = ''
      this.selectedMonth = new Date()
      this.updateMonthString()
      this.update()
    },
    prev() {
      this.$refs.calendar.prev()
      this.selectedMonth.setDate(0) // takes the date to the previous month
      this.update()
    },
    next() {
      this.$refs.calendar.next()
      this.selectedMonth.setMonth(this.selectedMonth.getMonth() + 1)
      this.update()
    },
    showEvent({ nativeEvent, event }) {
      const open = () => {
        this.selectedAction = event
        this.selectedElement = nativeEvent.target
        setTimeout(() => (this.selectedOpen = true), 10)
      }

      if (this.selectedOpen) {
        this.selectedOpen = false
        setTimeout(open, 10)
      } else {
        open()
      }

      nativeEvent.stopPropagation()
    },
    update() {
      this.updateMonthString()
      this.loadActions()
    },
    async loadActions() {
      const result = await Action.getAllFromApi(
        this.personType,
        this.personId,
        this.selectedMonth.getFullYear(),
        this.selectedMonth.getMonth()
      )

      this.events = result[0]
      this.$refs.calendar.checkChange()
    },
    updateMonthString() {
      this.monthString = getLocalMonthString(this.selectedMonth)
    }
  }
}
</script>

<style>
.event-name {
  color: rgba(0, 0, 0, 0.87);
  font-weight: 500;
}

.v-calendar-monthly .v-btn__content {
  color: grey !important;
}

.priority-icon {
  margin-right: -5px;
}
</style>
